import { useEffect, useState } from "react";
import { ArrowLeft, Sparkles } from "lucide-react";
import { adminFetch } from "../../lib/adminApi";

const inputCls =
    "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-paper placeholder:text-paper/30 transition-colors focus:border-acid focus:outline-none";

const EMPTY = {
    name: "",
    url: "",
    start_date: "",
    end_date: "",
    venue: "",
    city: "",
    description: "",
    attending: false,
    booth: "",
    published: true,
};

const FIELD = (label, key, props = {}) => ({ label, key, ...props });

const FIELDS = [
    FIELD("Event name", "name", { placeholder: "MJBizCon 2026" }),
    FIELD("Start date", "start_date", { type: "date" }),
    FIELD("End date (if multi-day)", "end_date", { type: "date" }),
    FIELD("Venue", "venue", { placeholder: "Las Vegas Convention Center" }),
    FIELD("City", "city", { placeholder: "Las Vegas, NV" }),
];

export default function EventEditor({ eventId, onBack }) {
    const [form, setForm] = useState(EMPTY);
    const [status, setStatus] = useState("");
    const [autofilling, setAutofilling] = useState(false);
    const [loading, setLoading] = useState(!!eventId);

    useEffect(() => {
        if (!eventId) return;
        adminFetch("/api/admin/events")
            .then((r) => (r.ok ? r.json() : []))
            .then((list) => {
                const ev = list.find((e) => e.id === eventId);
                if (ev) setForm({ ...EMPTY, ...ev });
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [eventId]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const autofill = async () => {
        if (!form.url.trim()) {
            setStatus("Paste the official event link first");
            return;
        }
        setAutofilling(true);
        setStatus("Reading the event page…");
        const res = await adminFetch("/api/admin/events/autofill", {
            method: "POST",
            body: JSON.stringify({ url: form.url.trim() }),
        });
        const data = await res.json().catch(() => ({}));
        setAutofilling(false);
        if (res.ok && data.name) {
            setForm((f) => ({ ...f, ...data }));
            setStatus("Auto-filled — check the details, then save.");
        } else {
            setStatus(typeof data.detail === "string" ? data.detail : "Auto-fill failed — fill it in manually");
        }
    };

    const save = async () => {
        if (!form.name.trim()) {
            setStatus("Event name is required");
            return;
        }
        setStatus("Saving…");
        const res = eventId
            ? await adminFetch(`/api/admin/events/${eventId}`, {
                  method: "PUT",
                  body: JSON.stringify(form),
              })
            : await adminFetch("/api/admin/events", {
                  method: "POST",
                  body: JSON.stringify(form),
              });
        if (res.ok) {
            setStatus("Saved — it's live on the events page.");
            setTimeout(onBack, 800);
        } else {
            const data = await res.json().catch(() => ({}));
            setStatus(typeof data.detail === "string" ? data.detail : "Save failed — try again");
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-svh items-center justify-center bg-ink">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-paper/50">Loading…</p>
            </div>
        );
    }

    return (
        <div data-testid="event-editor" className="min-h-svh bg-ink text-paper">
            <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
                <button
                    type="button"
                    data-testid="event-editor-back"
                    onClick={onBack}
                    className="group flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60 transition-colors hover:text-acid"
                >
                    <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                    All events
                </button>

                <h1 className="mt-8 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                    {eventId ? "Edit event" : "New event"}
                </h1>

                <div className="mt-8 border border-acid/40 bg-panel p-5">
                    <label htmlFor="event-url" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-paper/50">
                        Official event link
                    </label>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <input
                            id="event-url"
                            data-testid="event-url-input"
                            value={form.url}
                            onChange={(e) => set("url", e.target.value)}
                            className={inputCls}
                            placeholder="https://theofficialwebsite.com"
                        />
                        <button
                            type="button"
                            data-testid="event-autofill-button"
                            onClick={autofill}
                            disabled={autofilling}
                            className="flex shrink-0 items-center justify-center gap-2 bg-acid px-5 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-paper disabled:opacity-50"
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            {autofilling ? "Reading…" : "Auto-fill"}
                        </button>
                    </div>
                    <p className="mt-3 text-xs leading-relaxed text-fog">
                        Paste the link, hit Auto-fill — the details fill themselves in. Check them,
                        then save.
                    </p>
                </div>

                <div className="mt-8 space-y-5">
                    {FIELDS.map(({ label, key, ...props }) => (
                        <div key={key}>
                            <label htmlFor={`event-${key}`} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-paper/50">
                                {label}
                            </label>
                            <input
                                id={`event-${key}`}
                                data-testid={`event-input-${key}`}
                                value={form[key]}
                                onChange={(e) => set(key, e.target.value)}
                                className={inputCls}
                                {...props}
                            />
                        </div>
                    ))}
                    <div>
                        <label htmlFor="event-description" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-paper/50">
                            One-line description
                        </label>
                        <textarea
                            id="event-description"
                            data-testid="event-input-description"
                            value={form.description}
                            onChange={(e) => set("description", e.target.value)}
                            rows={3}
                            className={inputCls}
                            placeholder="What the event is, in one or two plain sentences"
                        />
                    </div>

                    <label className="flex items-center gap-3 border border-white/10 bg-panel px-4 py-3.5">
                        <input
                            type="checkbox"
                            data-testid="event-attending-toggle"
                            checked={form.attending}
                            onChange={(e) => set("attending", e.target.checked)}
                            className="h-4 w-4 accent-[#c8f542]"
                        />
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/70">
                            Triple O will be at this one
                        </span>
                    </label>

                    {form.attending && (
                        <div>
                            <label htmlFor="event-booth" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-paper/50">
                                Booth # (optional)
                            </label>
                            <input
                                id="event-booth"
                                data-testid="event-input-booth"
                                value={form.booth}
                                onChange={(e) => set("booth", e.target.value)}
                                className={inputCls}
                                placeholder="e.g. 4127"
                            />
                        </div>
                    )}

                    <label className="flex items-center gap-3 border border-white/10 bg-panel px-4 py-3.5">
                        <input
                            type="checkbox"
                            data-testid="event-published-toggle"
                            checked={form.published}
                            onChange={(e) => set("published", e.target.checked)}
                            className="h-4 w-4 accent-[#c8f542]"
                        />
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/70">
                            Visible on the site
                        </span>
                    </label>
                </div>

                {status && (
                    <p data-testid="event-editor-status" className="mt-6 font-mono text-[11px] uppercase tracking-[0.15em] text-acid">
                        {status}
                    </p>
                )}

                <button
                    type="button"
                    data-testid="event-save-button"
                    onClick={save}
                    className="mt-8 w-full bg-acid px-10 py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-ink transition-colors hover:bg-paper"
                >
                    {eventId ? "Save changes" : "Publish event"}
                </button>
            </div>
        </div>
    );
}
